import { Injectable } from '@angular/core';
import { User, UserRole } from '../models/user.model';
import { Class, CourseProgress, UpcomingClass, Announcement } from '../models/class.model';

@Injectable({
  providedIn: 'root'
})
export class MockDataService {

  // Mock Users
  private mockUsers: User[] = [
    {
      id: '1',
      documento: '12345678',
      nombre: 'Juan Carlos Pérez Rodríguez',
      email: 'juan.perez@continental.edu.pe',
      celular: '+51 987 654 321',
      cargo: 'alumno',
      contrato: 'CONT-2024-001',
      avatar: 'assets/img/avatars/student-avatar.jpg'
    },
    {
      id: '2',
      documento: '87654321',
      nombre: 'María González López',
      email: 'maria.gonzalez@continental.edu.pe',
      celular: '+51 987 123 456',
      cargo: 'instructor',
      avatar: 'assets/img/avatars/instructor-avatar.jpg'
    },
    {
      id: '3',
      documento: '11223344',
      nombre: 'Roberto Admin Sistema',
      email: 'roberto.admin@continental.edu.pe',
      celular: '+51 999 888 777',
      cargo: 'administrador',
      avatar: 'assets/img/avatars/admin-avatar.jpg'
    }
  ];

  // Mock Classes - Using dates relative to today
  private mockClasses: Class[] = this.generateMockClasses();

  private generateMockClasses(): Class[] {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    const threeDaysLater = new Date(today);
    threeDaysLater.setDate(threeDaysLater.getDate() + 3);

    return [
      {
        id: 'C001',
        titulo: 'Programación Orientada a Objetos - Clase 1',
        instructor: 'Ing. María González López',
        fecha: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 10, 0), // Hoy 10:00 AM
        horaInicio: '10:00',
        horaFin: '12:00',
        tipo: 'teoria',
        modalidad: 'presencial',
        ubicacion: 'Aula 301 - Edificio B',
        estado: 'completada',
        asistencia: 'asistio'
      },
      {
        id: 'C002',
        titulo: 'Taller de POO - Ejercicios Prácticos',
        instructor: 'Ing. María González López',
        fecha: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 14, 0), // Hoy 2:00 PM
        horaInicio: '14:00',
        horaFin: '16:00',
        tipo: 'taller',
        modalidad: 'presencial',
        ubicacion: 'Laboratorio 102',
        estado: 'en-curso'
      },
      {
        id: 'C003',
        titulo: 'Base de Datos Relacionales',
        instructor: 'Ing. Carlos Ramírez Soto',
        fecha: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 9, 0), // Mañana 9:00 AM
        horaInicio: '09:00',
        horaFin: '11:00',
        tipo: 'teoria',
        modalidad: 'virtual',
        urlVirtual: 'https://meet.google.com/abc-defg-hij',
        estado: 'programada'
      },
      {
        id: 'C004',
        titulo: 'Práctica SQL Avanzado',
        instructor: 'Ing. Carlos Ramírez Soto',
        fecha: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 15, 0), // Mañana 3:00 PM
        horaInicio: '15:00',
        horaFin: '17:00',
        tipo: 'practica',
        modalidad: 'presencial',
        ubicacion: 'Laboratorio 205',
        estado: 'programada'
      },
      {
        id: 'C005',
        titulo: 'Desarrollo Web Frontend',
        instructor: 'Ing. Ana Torres Medina',
        fecha: new Date(dayAfterTomorrow.getFullYear(), dayAfterTomorrow.getMonth(), dayAfterTomorrow.getDate(), 10, 0), // Pasado mañana
        horaInicio: '10:00',
        horaFin: '13:00',
        tipo: 'taller',
        modalidad: 'presencial',
        ubicacion: 'Aula 405',
        estado: 'programada'
      },
      {
        id: 'C006',
        titulo: 'Arquitectura de Software',
        fecha: new Date(threeDaysLater.getFullYear(), threeDaysLater.getMonth(), threeDaysLater.getDate(), 11, 0), // En 3 días
        horaInicio: '11:00',
        horaFin: '13:00',
        instructor: 'Ing. Pedro Sánchez',
        tipo: 'teoria',
        modalidad: 'virtual',
        urlVirtual: 'https://zoom.us/j/123456789',
        estado: 'programada'
      }
    ];
  }

  // Mock Progress
  private mockProgress: CourseProgress = {
    teoria: {
      horasCompletadas: 24,
      horasRequeridas: 40,
      porcentaje: 60
    },
    taller: {
      horasCompletadas: 18,
      horasRequeridas: 30,
      porcentaje: 60
    },
    practica: {
      horasCompletadas: 12,
      horasRequeridas: 25,
      porcentaje: 48
    },
    financiero: {
      horasCompletadas: 800,
      horasRequeridas: 1000,
      porcentaje: 80
    }
  };

  // Mock Announcements
  private mockAnnouncements: Announcement[] = [
    {
      id: 'A001',
      titulo: 'Recordatorio de Pago',
      contenido: 'La próxima cuota vence el 31 de Octubre. Recuerda realizar tu pago a tiempo.',
      fecha: new Date(2025, 0, 25),
      tipo: 'warning',
      icono: 'mat:payment'
    },
    {
      id: 'A002',
      titulo: 'Nueva Modalidad Virtual',
      contenido: 'A partir del próximo mes, algunas clases teóricas estarán disponibles en modalidad virtual.',
      fecha: new Date(2025, 0, 23),
      tipo: 'info',
      icono: 'mat:video_call'
    },
    {
      id: 'A003',
      titulo: 'Felicitaciones por tu Progreso',
      contenido: 'Has completado el 60% de tus horas de teoría. ¡Sigue así!',
      fecha: new Date(2025, 0, 20),
      tipo: 'success',
      icono: 'mat:celebration'
    }
  ];

  constructor() {}

  // Get user by credentials
  getUserByCredentials(username: string, password: string, userType: string): User | null {
    // Mock authentication - accept any username/password combo
    if (userType === 'alumno') {
      return this.mockUsers.find(u => u.cargo === 'alumno') || null;
    } else {
      // For 'gestion', return either instructor or admin
      return this.mockUsers.find(u => u.cargo === 'administrador') || null;
    }
  }

  // Get all classes for student
  getStudentClasses(): Class[] {
    return this.mockClasses;
  }

  // Get upcoming classes
  getUpcomingClasses(limit: number = 5): UpcomingClass[] {
    const now = new Date();
    return this.mockClasses
      .filter(c => c.fecha >= now && c.estado === 'programada')
      .sort((a, b) => a.fecha.getTime() - b.fecha.getTime())
      .slice(0, limit)
      .map(c => ({
        id: c.id,
        titulo: c.titulo,
        instructor: c.instructor,
        fecha: c.fecha,
        hora: c.horaInicio,
        tipo: c.tipo,
        modalidad: c.modalidad
      }));
  }

  // Get student progress
  getStudentProgress(): CourseProgress {
    return this.mockProgress;
  }

  // Get announcements
  getAnnouncements(): Announcement[] {
    return this.mockAnnouncements.sort((a, b) =>
      b.fecha.getTime() - a.fecha.getTime()
    );
  }

  // Get classes by date range
  getClassesByDateRange(startDate: Date, endDate: Date): Class[] {
    return this.mockClasses.filter(c =>
      c.fecha >= startDate && c.fecha <= endDate
    );
  }
}
